'use client';

import { useEffect, useRef, useState } from 'react';
import { MediaImage } from './MediaImage';

type TrendMediaProps = {
  src: string | null | undefined;
  poster?: string | null;
  alt: string;
  isVideo: boolean;
  imgClassName?: string;
};

export function TrendMedia({
  src,
  poster,
  alt,
  isVideo,
  imgClassName,
}: TrendMediaProps) {
  if (!isVideo) {
    return <MediaImage src={poster || src} alt={alt} imgClassName={imgClassName} />;
  }
  return <TrendVideo src={src} poster={poster} alt={alt} />;
}

function TrendVideo({
  src,
  poster,
  alt,
}: {
  src: string | null | undefined;
  poster?: string | null;
  alt: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setIsNearViewport(entry.isIntersecting);
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      setShouldPlay(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          setShouldPlay(entry.isIntersecting && entry.intersectionRatio >= 0.35);
        }
      },
      { threshold: [0, 0.35, 0.6] },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [isNearViewport]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (shouldPlay) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [shouldPlay]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden bg-white/[0.04]"
    >
      {poster ? (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img
          src={poster}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}
      {isNearViewport && src ? (
        <video
          ref={videoRef}
          src={src}
          poster={poster || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
          aria-label={alt}
        />
      ) : null}
    </div>
  );
}
